import { useEffect, useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Button,
  cn,
  Icon,
  Input,
  SearchableCombobox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@nexus/ui";
import type { ScheduledJobState } from "@/features/scheduled-jobs/api/scheduled-jobs.api";
import { EventsTimeRangeControl } from "@/features/events/components/EventsTimeRangeControl";
import { useDebouncedCallback } from "@/hooks/useDebouncedCallback";
import type {
  ScheduledJobHealthFilter,
  ScheduledJobsViewFilters,
} from "../lib/scheduled-job-filters";

interface ScheduledJobsFilterBarProps {
  filters: ScheduledJobsViewFilters;
  eventTypeOptions: string[];
  resultCount: number | null;
  hasActiveFilters: boolean;
  onFiltersChange: (next: ScheduledJobsViewFilters) => void;
  onClearFilters: () => void;
}

const ALL = "all";
const SEARCH_DEBOUNCE_MS = 300;
const CONTROL_HEIGHT = "h-8";
const CHIP_CLASS =
  "inline-flex items-center gap-1.5 rounded-sm border border-primary/40 bg-primary/5 pl-2 pr-1 text-xs text-foreground";
const FILTER_TRIGGER_CLASS =
  "flex h-8 items-center justify-between gap-2 rounded-sm border border-input-border bg-input-background px-3 py-1 text-xs text-foreground outline-none transition-colors focus-visible:ring-1 focus-visible:ring-ring";
const ACTIVE_TRIGGER_CLASS = "border-primary/40 bg-primary/5 text-foreground";

const STATE_OPTIONS: ScheduledJobState[] = [
  "ENABLED",
  "PAUSED",
  "DISABLED",
  "UPDATE_FAILED",
  "UNKNOWN",
];

const HEALTH_OPTIONS: ScheduledJobHealthFilter[] = [
  "healthy",
  "failing",
  "overdue",
  "untracked",
];

/**
 * Same contract as BigQuery's bar: window scopes the fetch; search / state /
 * health / eventType narrow the loaded list. Clear-all leaves the window alone.
 */
export function ScheduledJobsFilterBar({
  filters,
  eventTypeOptions,
  resultCount,
  hasActiveFilters,
  onFiltersChange,
  onClearFilters,
}: ScheduledJobsFilterBarProps) {
  const { t, i18n } = useTranslation();
  const [searchInput, setSearchInput] = useState(filters.q ?? "");
  const { debounced: debouncedSearchChange, cancel: cancelSearchChange } = useDebouncedCallback(
    (value: string) => {
      const trimmed = value.trim();
      onFiltersChange({ ...filters, q: trimmed.length > 0 ? trimmed : undefined });
    },
    SEARCH_DEBOUNCE_MS,
  );

  useEffect(() => {
    setSearchInput(filters.q ?? "");
    cancelSearchChange();
  }, [filters.q, cancelSearchChange]);

  const allEventsLabel = t("admin.scheduledJobs.filters.allEvents", "All events");
  const eventItems = useMemo(() => [ALL, ...eventTypeOptions], [eventTypeOptions]);

  const stateLabel = (state: ScheduledJobState | typeof ALL): string => {
    if (state === ALL) return t("admin.scheduledJobs.filters.anyState", "Any state");
    return t(`admin.scheduledJobs.state.${state}`, state);
  };

  const healthLabel = (health: ScheduledJobHealthFilter | typeof ALL): string => {
    if (health === ALL) return t("admin.scheduledJobs.filters.anyHealth", "Any health");
    switch (health) {
      case "healthy":
        return t("admin.scheduledJobs.health.healthy", "Healthy");
      case "failing":
        return t("admin.scheduledJobs.filters.healthFailing", "Failing");
      case "overdue":
        return t("admin.scheduledJobs.health.overdue", "Overdue");
      case "untracked":
        return t("admin.scheduledJobs.health.untracked", "Not tracked");
    }
  };

  return (
    <div className="flex shrink-0 flex-wrap items-center gap-2">
      <EventsTimeRangeControl
        value={filters.range}
        onChange={(range) => onFiltersChange({ ...filters, range })}
      />

      <div className="relative flex items-center">
        <Icon
          name="search"
          size="xs"
          className="pointer-events-none absolute left-2 text-muted-foreground"
          aria-hidden
        />
        <Input
          value={searchInput}
          onChange={(event) => {
            setSearchInput(event.target.value);
            debouncedSearchChange(event.target.value);
          }}
          aria-label={t("admin.scheduledJobs.filters.searchLabel", "Search jobs")}
          placeholder={t(
            "admin.scheduledJobs.filters.searchPlaceholder",
            "Job id or event type…",
          )}
          className={cn(CONTROL_HEIGHT, "w-[220px] rounded-sm pl-7 text-xs focus-visible:ring-0")}
        />
      </div>

      <Select
        value={filters.state ?? ALL}
        onValueChange={(value) =>
          onFiltersChange({
            ...filters,
            state: value === ALL ? undefined : (value as ScheduledJobState),
          })
        }
      >
        <SelectTrigger
          aria-label={t("admin.scheduledJobs.filters.stateLabel", "Filter by state")}
          className={cn(
            CONTROL_HEIGHT,
            "w-[150px] rounded-sm text-xs",
            filters.state != null && ACTIVE_TRIGGER_CLASS,
          )}
        >
          <SelectValue placeholder={t("admin.scheduledJobs.filters.anyState", "Any state")} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{stateLabel(ALL)}</SelectItem>
          {STATE_OPTIONS.map((state) => (
            <SelectItem key={state} value={state}>
              {stateLabel(state)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={filters.health ?? ALL}
        onValueChange={(value) =>
          onFiltersChange({
            ...filters,
            health: value === ALL ? undefined : (value as ScheduledJobHealthFilter),
          })
        }
      >
        <SelectTrigger
          aria-label={t("admin.scheduledJobs.filters.healthLabel", "Filter by health")}
          className={cn(
            CONTROL_HEIGHT,
            "w-[150px] rounded-sm text-xs",
            filters.health != null && ACTIVE_TRIGGER_CLASS,
          )}
        >
          <SelectValue placeholder={t("admin.scheduledJobs.filters.anyHealth", "Any health")} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{healthLabel(ALL)}</SelectItem>
          {HEALTH_OPTIONS.map((health) => (
            <SelectItem key={health} value={health}>
              {healthLabel(health)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <SearchableCombobox<string>
        items={eventItems}
        value={filters.eventType ?? ALL}
        onSelect={(eventType) =>
          onFiltersChange({
            ...filters,
            eventType: eventType === ALL ? undefined : eventType,
          })
        }
        getKey={(eventType) => eventType}
        getLabel={(eventType) => (eventType === ALL ? allEventsLabel : eventType)}
        ariaLabel={t("admin.scheduledJobs.filters.eventLabel", "Filter by event")}
        searchPlaceholder={t("admin.scheduledJobs.filters.searchEvents", "Search events…")}
        emptyMessage={t(
          "admin.scheduledJobs.filters.noEventsMatch",
          "No events match your search.",
        )}
        renderItem={(eventType) => (
          <span className="min-w-0 flex-1 truncate font-mono text-[11px]">
            {eventType === ALL ? allEventsLabel : eventType}
          </span>
        )}
        renderTrigger={({ selected, open }) => (
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-label={t("admin.scheduledJobs.filters.eventLabel", "Filter by event")}
            className={cn(
              FILTER_TRIGGER_CLASS,
              "w-[200px]",
              filters.eventType != null && ACTIVE_TRIGGER_CLASS,
              open && "border-input-focus-border",
            )}
          >
            <span className="truncate text-left font-mono text-[11px]">
              {selected === ALL || selected == null ? allEventsLabel : selected}
            </span>
            <ChevronDown className="size-4 shrink-0 opacity-50" aria-hidden />
          </button>
        )}
      />

      {filters.q != null && filters.q.trim().length > 0 ? (
        <FilterChip
          label={t("admin.scheduledJobs.filters.searchChip", "Search: {{q}}", { q: filters.q })}
          removeLabel={t("admin.scheduledJobs.filters.removeSearch", "Remove the search filter")}
          onRemove={() => onFiltersChange({ ...filters, q: undefined })}
        />
      ) : null}

      {filters.state != null ? (
        <FilterChip
          label={t("admin.scheduledJobs.filters.stateChip", "State: {{state}}", {
            state: stateLabel(filters.state),
          })}
          removeLabel={t("admin.scheduledJobs.filters.removeState", "Remove the state filter")}
          onRemove={() => onFiltersChange({ ...filters, state: undefined })}
        />
      ) : null}

      {filters.health != null ? (
        <FilterChip
          label={t("admin.scheduledJobs.filters.healthChip", "Health: {{health}}", {
            health: healthLabel(filters.health),
          })}
          removeLabel={t("admin.scheduledJobs.filters.removeHealth", "Remove the health filter")}
          onRemove={() => onFiltersChange({ ...filters, health: undefined })}
        />
      ) : null}

      {filters.eventType != null ? (
        <FilterChip
          label={t("admin.scheduledJobs.filters.eventChip", "Event: {{eventType}}", {
            eventType: filters.eventType,
          })}
          removeLabel={t("admin.scheduledJobs.filters.removeEvent", "Remove the event filter")}
          onRemove={() => onFiltersChange({ ...filters, eventType: undefined })}
        />
      ) : null}

      {hasActiveFilters ? (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 text-xs text-primary"
          onClick={onClearFilters}
        >
          {t("admin.scheduledJobs.filters.clear", "Clear filters")}
        </Button>
      ) : null}

      <span className="flex-1" />

      {resultCount == null ? null : (
        <span className="text-xs tabular-nums text-muted-foreground">
          {t("admin.scheduledJobs.filters.resultCount", "{{count}} jobs", {
            count: resultCount,
            formattedCount: resultCount.toLocaleString(i18n.language),
          })}
        </span>
      )}
    </div>
  );
}

function FilterChip({
  label,
  removeLabel,
  onRemove,
}: {
  label: string;
  removeLabel: string;
  onRemove: () => void;
}) {
  return (
    <span className={cn(CONTROL_HEIGHT, CHIP_CLASS)}>
      <Icon name="filter" size="xs" className="shrink-0 text-primary" aria-hidden />
      {label}
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        className="size-5 rounded-sm"
        aria-label={removeLabel}
        onClick={onRemove}
      >
        <Icon name="x" size="xs" aria-hidden />
      </Button>
    </span>
  );
}
